/**
 * Speech output — Web Speech Synthesis for voice responses
 */

class SpeechOutput {
  constructor() {
    this.synth = window.speechSynthesis;
    this.voice = null;
    this.isSpeaking = false;
    this._voicesReady = false;

    // Select a good voice once voices are loaded
    this._selectVoice();
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => this._selectVoice();
    }
  }

  _selectVoice() {
    const voices = this.synth.getVoices();
    if (voices.length === 0) return;

    this._voicesReady = true;

    // Priority list — best quality voices first
    const preferred = [
      'Google UK English Female',
      'Google UK English Male',
      'Samantha',            // macOS
      'Daniel',              // macOS British
      'Microsoft Libby',     // Edge
      'Karen',               // macOS Australian
      'Microsoft Zira',
      'Microsoft Mark',
    ];

    // First pass: check preferred list
    for (const name of preferred) {
      const found = voices.find(v => v.name.includes(name));
      if (found) {
        this.voice = found;
        console.log(`[SpeechOutput] Selected voice: ${found.name}`);
        return;
      }
    }

    // Second pass: any voice with "Natural" in the name
    const natural = voices.find(v => v.name.includes('Natural') && v.lang.startsWith('en'));
    if (natural) {
      this.voice = natural;
      console.log(`[SpeechOutput] Selected natural voice: ${natural.name}`);
      return;
    }

    // Fall back to first English voice
    this.voice = voices.find(v => v.lang.startsWith('en')) || voices[0];
    console.log(`[SpeechOutput] Fallback voice: ${this.voice?.name}`);
  }

  /**
   * Speak a text string with a short delay for natural pacing.
   * @param {string} text - The text to speak
   * @param {number} delay - Delay in ms before speaking (default 200)
   * @returns {Promise<void>} Resolves when speech finishes
   */
  speak(text, delay = 200) {
    return new Promise((resolve) => {
      if (!text || !this.synth) {
        resolve();
        return;
      }

      // Cancel any ongoing speech
      this.synth.cancel();

      // Small pause so voice feels like a reaction to the visual
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text);

        if (this.voice) {
          utterance.voice = this.voice;
        }

        utterance.rate = 1.05;
        utterance.pitch = 1.0;
        utterance.volume = 0.9;

        utterance.onend = () => {
          this.isSpeaking = false;
          resolve();
        };

        utterance.onerror = () => {
          this.isSpeaking = false;
          resolve();
        };

        this.isSpeaking = true;
        this.synth.speak(utterance);
      }, delay);
    });
  }

  /**
   * Stop any current speech
   */
  stop() {
    if (this.synth) {
      this.synth.cancel();
      this.isSpeaking = false;
    }
  }

  /**
   * Check if the browser supports speech synthesis
   */
  get isSupported() {
    return !!this.synth;
  }
}

export { SpeechOutput };
export default SpeechOutput;
